'use client';

import { useMemo, type CSSProperties } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useWikiSource, useBlockPart, type WikiTreeNode } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { cardReset } from '../_shared/styles';
import { currentWikiSlug, flattenWikiTree } from './wikiSlug';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Previous / next pager at the foot of a wiki doc — the sequential
 * "keep reading" affordance every documentation site has. Flattens the
 * tree into reading order and links the siblings either side of the
 * current page. Renders nothing when there's no bound page or the page
 * sits alone in the tree.
 */
export interface WikiPrevNextProps {
  /** Label above the previous link. Default "Previous". */
  prevLabel?: string;
  /** Label above the next link. Default "Next". */
  nextLabel?: string;
}

export function WikiPrevNext({
  prevLabel: authoredPrevLabel,
  nextLabel: authoredNextLabel,
}: WikiPrevNextProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const prevLabel = authoredPrevLabel ?? t('shared.nav.previous');
  const nextLabel = authoredNextLabel ?? t('shared.nav.next');
  const wiki = useWikiSource();
  const pathname = usePathname() ?? '';
  const slug = useMemo(() => currentWikiSlug(pathname), [pathname]);

  const treeState = useAsyncResource<WikiTreeNode[]>(async () => {
    if (!wiki) return null;
    const tree = await wiki.getTree();
    return tree.length > 0 ? tree : null;
  }, [wiki]);

  const { prev, next } = useMemo(() => {
    if (treeState.kind !== 'loaded' || !slug) return { prev: null, next: null };
    const flat = flattenWikiTree(treeState.data);
    const i = flat.findIndex((n) => n.slug === slug);
    if (i < 0) return { prev: null, next: null };
    return { prev: i > 0 ? flat[i - 1] : null, next: i < flat.length - 1 ? flat[i + 1] : null };
  }, [treeState, slug]);

  if (!slug || (!prev && !next)) return null;

  return (
    <BlockRoot as="nav" aria-label={t('shared.nav.pagination')} style={navStyle}>
      <div style={{ flex: 1 }}>
        {prev ? (
          <Card asChild>
            <Link
              href={`/wiki/${prev.slug}`}
              {...part('pager', 0)}
              style={{ ...cardReset, ...cardStyle, alignItems: 'flex-start' }}
            >
              <span style={dirStyle}>← {prevLabel}</span>
              <span style={pageTitleStyle}>{prev.title}</span>
            </Link>
          </Card>
        ) : null}
      </div>
      <div style={{ flex: 1 }}>
        {next ? (
          <Card asChild>
            <Link
              href={`/wiki/${next.slug}`}
              {...part('pager', 1)}
              style={{ ...cardReset, ...cardStyle, alignItems: 'flex-end', textAlign: 'right' }}
            >
              <span style={dirStyle}>{nextLabel} →</span>
              <span style={pageTitleStyle}>{next.title}</span>
            </Link>
          </Card>
        ) : null}
      </div>
    </BlockRoot>
  );
}

const navStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-4, 16px)',
  marginTop: 'var(--space-8, 32px)',
  paddingTop: 'var(--space-6, 24px)',
  borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
};

// This block's own pager-card surface, spread after the shared `cardReset`
// onto `<Card asChild>`.
const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 'var(--space-4, 16px)',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.10))',
  textDecoration: 'none',
  height: '100%',
};

const dirStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 13px)',
  color: 'var(--color-text-tertiary, #a1a1aa)',
};

const pageTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-md, 15px)',
  fontWeight: 600,
  color: 'var(--color-text-brand, var(--color-brand-700, #2b3eb0))',
};
